import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  styled,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Link,
  CircularProgress,
  TextField,
} from "@mui/material";
import { Regulation } from "@services/regulations";
import {
  PenaltiesData,
  TrainingsData,
  FineAnalysisData,
  RegulationSourceDocument,
  RegulationSourceDocumentSection,
  getPenaltiesData,
  getTrainingsData,
  getFineAnalysisData,
  getRegulationSourceDocuments,
} from "@services/regulationDetails";
import { MindmapVisualization } from "@components/Mindmap";
import { generateMindmap, MindmapResponse } from "@services/mindmap";
import { formatCurrency, formatDate } from "@utils/formatUtils";
import { getSelectedCountryCode } from "@utils/countryUtils";
import { getSelectedFacilityFromSession } from "@utils/facilityUtils";
import scalesIcon from "@assets/regulation-library/scales.svg";
import documentCopyIcon from "@assets/regulation-library/document-copy.svg";
import chevronDownIcon from "@assets/regulation-library/chevron-down.svg";

const ContentContainer = styled(Box)({
  backgroundColor: "#fdffff",
  height: "98vh",
  borderRadius: "12px",
  border: "1px solid #99d4e1",
  boxShadow: "inset 8px 2px 58.4px 0px rgba(191,242,255,0.2)",
  position: "relative",
  overflow: "hidden",
  width: "100%",
  boxSizing: "border-box",
});

const ContentWrapper = styled(Box)(({ theme }) => ({
  padding: "40px 64px 48px",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  overflowY: "auto",
  boxSizing: "border-box",
  [theme.breakpoints.down("md")]: {
    padding: "24px 16px 32px",
  },
}));

const MainContent = styled(Box)({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

// const BreadcrumbContainer = styled(Box)({
//   display: "flex",
//   alignItems: "center",
//   gap: "4px",
//   padding: "16px 16px",
// });
const Breadcrumbs = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "13px",
  color: "#8aa0a8",
});
const BreadcrumbLink = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "13px",
  fontWeight: 500,
  lineHeight: "normal",
  color: "#0a829d",
  cursor: "pointer",
  "&:hover": {
    color: "#065b70",
  },
  "&.active": {
    color: "#065b70",
    fontWeight: 600,
    cursor: "default",
  },
});
// const BreadcrumbButton = styled(Button)({
//   backgroundColor: "transparent",
//   color: "#262626",
//   fontSize: "12px",
//   fontFamily: "Inter, sans-serif",
//   fontWeight: 400,
//   textTransform: "none",
//   padding: "4px 8px",
//   borderRadius: "4px",
//   minWidth: "auto",
//   "&:hover": {
//     backgroundColor: "rgba(0,0,0,0.04)",
//   },
// });

// const BreadcrumbCurrent = styled(Typography)({
//   fontSize: "12px",
//   fontFamily: "Inter, sans-serif",
//   fontWeight: 400,
//   color: "#086e86",
//   padding: "4px 8px",
// });

// const ChevronIcon = styled(Box)({
//   width: "12px",
//   height: "12px",
//   display: "flex",
//   alignItems: "center",
//   justifyContent: "center",
// });

const HeaderTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "4px",
});

const RegulationLogo = styled(Box)({
  // width: "91px",
  height: "38px",
  display: "flex",
  // alignItems: "center",
  // justifyContent: "center",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
});

const Title = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 700,
  fontSize: "24px",
  lineHeight: "1.5",
  letterSpacing: "-0.4px",
  color: "#262626",
});

const Subtitle = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "1.5",
  letterSpacing: "-0.4px",
  color: "#737373",
});

const TabsContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "0px",
  paddingTop: "12px",
  paddingBottom: "0px",
  justifyContent: "flex-start",
  width: "100%",
  borderBottom: "1px solid #35d7e4",
});

const TabButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "$active",
})<{ $active?: boolean }>(({ $active }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "normal",
  letterSpacing: "normal",
  textTransform: "none",
  color: $active ? "#0a829d" : "#5c6b73",
  backgroundColor: "transparent",
  borderBottom: $active ? "2px solid #0a829d" : "2px solid transparent",
  borderRadius: 0,
  padding: "8px 12px",
  minWidth: "auto",
  margin: 0,
  "&:hover": {
    backgroundColor: "transparent",
    color: "#0a829d",
  },
}));

const TabContentContainer = styled(Box)({
  // padding: "16px",
});

// const FormSection = styled(Box)({
//   backgroundColor: "#fffdfd",
//   border: "1px solid #ccf5ff",
//   borderRadius: "12px",
//   padding: "16px",
//   boxShadow: "0px 4px 16px 0px rgba(5,89,108,0.08)",
//   marginBottom: "24px",
// });

// const FormHeader = styled(Box)({
//   borderBottom: "1px solid #35d7e4",
//   paddingBottom: "16px",
//   marginBottom: "16px",
// });

// const FormTitle = styled(Typography)({
//   fontFamily: "Rethink Sans, sans-serif",
//   fontWeight: 500,
//   fontSize: "16px",
//   color: "#065b70",
//   marginBottom: "8px",
// });

// const FormSubtitle = styled(Typography)({
//   fontFamily: "Rethink Sans, sans-serif",
//   fontWeight: 500,
//   fontSize: "12px",
//   color: "#065b70",
// });

// const InputRow = styled(Box)({
//   backgroundColor: "#fcfcfc",
//   border: "1px solid #ccf5ff",
//   borderRadius: "12px",
//   padding: "12px",
//   display: "flex",
//   gap: "16px",
//   alignItems: "center",
// });

// const InputField = styled(Box)({
//   flex: 1,
//   backgroundColor: "#fdffff",
//   border: "1px solid #99d4e1",
//   borderRadius: "8px",
//   padding: "8px",
//   display: "flex",
//   alignItems: "center",
// });

// const InputText = styled(Typography)({
//   fontFamily: "Rethink Sans, sans-serif",
//   fontWeight: 400,
//   fontSize: "14px",
//   color: "#262626",
//   flex: 1,
// });

const ResultsSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
});

function DetailSectionHeading({ title, note }: { title: string; note?: string }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", p: "8px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Box component="img" src={scalesIcon} alt="" width={20} height={20} />
        <Typography
          sx={{
            fontFamily: "Rethink Sans, sans-serif",
            fontWeight: 700,
            fontSize: 20,
            color: "#065b70",
            lineHeight: "normal",
          }}
        >
          {title}
        </Typography>
      </Box>
      {note ? (
        <Typography
          sx={{
            fontFamily: "Rethink Sans, sans-serif",
            fontSize: 14,
            lineHeight: "20px",
            color: "#065b70",
          }}
        >
          {note}
        </Typography>
      ) : null}
    </Box>
  );
}

const ResultAccordion = styled(Accordion)({
  backgroundColor: "#ffffff",
  border: "1px solid #9ee5f4",
  borderRadius: "4px !important",
  boxShadow: "0px 4px 4px rgba(111, 211, 232, 0.15)",
  margin: "0 !important",
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    margin: "0 !important",
  },
});

const ResultAccordionSummary = styled(AccordionSummary)({
  padding: "8px",
  minHeight: "37px",
  boxSizing: "border-box",
  gap: "8px",
  color: "#065b70",
  "&.Mui-expanded": {
    minHeight: "37px",
  },
  "& .MuiAccordionSummary-content": {
    alignItems: "center",
    margin: "0",
    gap: "8px",
  },
  "& .MuiAccordionSummary-content.Mui-expanded": {
    margin: "0",
  },
  "& .MuiAccordionSummary-expandIconWrapper": {
    color: "#065b70",
    marginRight: 0,
  },
});

const ResultTitle = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "16px",
  lineHeight: "normal",
  letterSpacing: "normal",
  color: "#262626",
  flex: 1,
});

// const BackButton = styled(Button)({
//   backgroundColor: "#0a829d",
//   color: "white",
//   fontFamily: "Rethink Sans, sans-serif",
//   fontWeight: 500,
//   textTransform: "none",
//   borderRadius: "8px",
//   padding: "8px 16px",
//   "&:hover": {
//     backgroundColor: "#065b70",
//   },
//   alignSelf: "flex-start",
//   marginBottom: "16px",
// });

type RegulationTab =
  | "compliance_mindmap"
  | "penalties"
  | "trainings"
  | "fine_analysis"
  | "source_documents";

interface RegulationDetailProps {
  regulation: Regulation;
  onBack?: () => void;
}

function isHipaaRegulation(regulation?: Regulation): boolean {
  const value = `${regulation?.name ?? ""} ${regulation?.description ?? ""}`;
  return /\bHIPAA\b/i.test(value);
}

function sourceDocumentKey(doc: RegulationSourceDocument, index: number): string {
  return String(doc.id ?? doc.document_number ?? index);
}

function sourceSectionKey(
  section: RegulationSourceDocumentSection,
  index: number,
): string {
  return String(section.id ?? section.anchor_id ?? section.heading ?? index);
}

function formatHeadingPath(path: RegulationSourceDocumentSection["heading_path"]): string {
  if (Array.isArray(path)) return path.filter(Boolean).join(" / ");
  if (typeof path === "string") return path;
  return "";
}

function getSourceSectionText(section: RegulationSourceDocumentSection): string {
  return section.text?.trim() || section.excerpt?.trim() || "";
}

function matchesSourceFilter(
  doc: RegulationSourceDocument,
  filter: string,
): boolean {
  const q = filter.trim().toLowerCase();
  if (!q) return true;
  const docHaystack = [
    doc.title,
    doc.document_number,
    doc.citation,
    doc.source_system,
    doc.publication_date,
    doc.effective_date,
    ...(doc.cfr_references ?? []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  if (docHaystack.includes(q)) return true;
  return (doc.sections ?? []).some((section) =>
    [
      section.anchor_id,
      section.heading,
      formatHeadingPath(section.heading_path),
      getSourceSectionText(section),
      ...(section.cfr_references ?? []),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(q),
  );
}

// const ChevronRightIcon = () => (
<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
  <path
    d="M4.5 2L8 6L4.5 10"
    stroke="currentColor"
    strokeWidth="1"
    fill="none"
  />
</svg>;
// );

const ChevronDownIcon = () => (
  <Box component="img" src={chevronDownIcon} alt="" width={20} height={20} />
);

const CertificateIcon = () => (
  <Box component="img" src={documentCopyIcon} alt="" width={20} height={20} />
);

// const mockResultItems = [
//   "Enforcement Rules",
//   "Penalty tiers",
//   "Mitigation Factors",
//   "Procedural Safeguards",
//   "Official Sources",
//   "Penalties & Total Exposure",
//   "Entity Details",
//   "Raw JSON Response",
// ];

export default function RegulationDetail({
  regulation,
  onBack,
}: RegulationDetailProps) {
  const [activeTab, setActiveTab] =
    useState<RegulationTab>("compliance_mindmap");
  const [expandedResults, setExpandedResults] = useState<
    Record<string, boolean>
  >({});
  const [penaltiesData, setPenaltiesData] = useState<PenaltiesData | null>(
    null
  );
  const [trainingsData, setTrainingsData] = useState<TrainingsData | null>(
    null
  );
  const [fineAnalysisData, setFineAnalysisData] =
    useState<FineAnalysisData | null>(null);
  const [mindmapData, setMindmapData] = useState<MindmapResponse | null>(null);
  const [sourceDocuments, setSourceDocuments] =
    useState<RegulationSourceDocument[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [mindmapLoading, setMindmapLoading] = useState(false);
  const [mindmapError, setMindmapError] = useState<string | null>(null);
  const [sourceDocumentsLoading, setSourceDocumentsLoading] = useState(false);
  const [sourceDocumentsError, setSourceDocumentsError] = useState<string | null>(
    null,
  );
  const [sourceDocumentFilter, setSourceDocumentFilter] = useState("");
  const [selectedFacility, setSelectedFacility] = useState<any>(
    getSelectedFacilityFromSession()
  );
  const showSourceDocumentsTab =
    isHipaaRegulation(regulation) || (sourceDocuments?.length ?? 0) > 0;

  // Listen for facility changes in session storage
  useEffect(() => {
    const checkFacilityChange = () => {
      const currentFacility = getSelectedFacilityFromSession();
      if (currentFacility?.facility_id !== selectedFacility?.facility_id) {
        console.log("Facility changed, updating...", currentFacility);
        setSelectedFacility(currentFacility);
      }
    };

    const interval = setInterval(checkFacilityChange, 1000);
    window.addEventListener("storage", checkFacilityChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", checkFacilityChange);
    };
  }, [selectedFacility?.facility_id]);

  useEffect(() => {
    let cancelled = false;

    // Generate mindmap when component loads or facility context changes
    const generateMindmapData = async () => {
      try {
        setMindmapLoading(true);
        setMindmapError(null);

        if (process.env.NODE_ENV === "development") {
          console.log(
            selectedFacility?.facility_id != null
              ? `Generating mindmap for facility id: ${selectedFacility.facility_id}`
              : "Generating mindmap (no facility in session; using default facility context)"
          );
        }

        const mindmapPayload = {
          regulation_name: regulation?.name || "",
          org_name: selectedFacility?.org_name || "Sample Organization",
          maxPrimarySpokes: 8,
          includeDetails: true,
          includeCrossLinks: false,
          facility_context: {
            facilityType:
              selectedFacility?.facility_type || "Acute Care Hospital (IPPS)",
            state: selectedFacility?.state || "TX",
            cityOrCounty: selectedFacility?.city || "Dallas",
            country: "USA",
          },
        };

        const mindmapResponse = await generateMindmap(mindmapPayload);
        if (!cancelled) {
          setMindmapData(mindmapResponse);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error generating mindmap:", error);
          setMindmapError(
            error instanceof Error ? error.message : "Failed to generate mindmap"
          );
        }
      } finally {
        if (!cancelled) {
          setMindmapLoading(false);
        }
      }
    };

    void generateMindmapData().catch((error) => {
      if (!cancelled) {
        console.error("Error generating mindmap (unhandled):", error);
        setMindmapError(
          error instanceof Error ? error.message : "Failed to generate mindmap"
        );
        setMindmapLoading(false);
      }
    });

    // Call API for penalties, trainings and fine analysis
    const penaltiesPayload = {
      country: getSelectedCountryCode(),
      regulation: regulation?.name || "",
      role: "Compliance Manager",
      subject: "Penalties",
      question: "What penalties are specified?",
      response_language: "English",
      instructions: "Provide the FULL text of each section",
      output_format: "JSON",
      org_name: "test",
    };

    (async () => {
      try {
        const penalties = await getPenaltiesData(penaltiesPayload as any);
        setPenaltiesData(penalties);
      } catch (e) {
        console.error(e);
        setPenaltiesData(null);
      }
    })();

    const trainingsPayload = {
      country: getSelectedCountryCode(),
      regulation: regulation?.name || "",
      role: "Compliance Manager",
      subject: "Training Resources",
      question: "What training programs are available for compliance?",
      response_language: "English",
      instructions:
        "Provide comprehensive training resources with URLs for all organizational roles",
      output_format: "JSON",
      org_name: "test",
    };

    (async () => {
      try {
        const trainings = await getTrainingsData(trainingsPayload as any);
        setTrainingsData(trainings);
      } catch (e) {
        console.error(e);
        setTrainingsData(null);
      }
    })();

    const defaultPayload = {
      country: getSelectedCountryCode(),
      regulation: regulation?.name || "",
      role: "Compliance Manager",
      subject: "Violation Fines",
      question: "What fines and settlements have been imposed for violations?",
      response_language: "English",
      instructions:
        "Provide comprehensive historical data on violation fines and settlements by year",
      output_format: "JSON",
      org_name: "test",
    };

    (async () => {
      try {
        setLoading(true);
        const data = await getFineAnalysisData(defaultPayload);
        setFineAnalysisData(data);
      } catch (e) {
        console.error(e);
        setFineAnalysisData(null);
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    regulation?.name,
    selectedFacility?.facility_id,
    selectedFacility?.org_name,
    selectedFacility?.facility_type,
    selectedFacility?.state,
    selectedFacility?.city,
  ]);

  useEffect(() => {
    let cancelled = false;

    if (!isHipaaRegulation(regulation)) {
      setSourceDocuments(null);
      setSourceDocumentsError(null);
      return () => {
        cancelled = true;
      };
    }

    const loadSourceDocuments = async () => {
      try {
        setSourceDocumentsLoading(true);
        setSourceDocumentsError(null);
        const response = await getRegulationSourceDocuments({
          regulation: regulation.name,
        });
        if (!cancelled) {
          setSourceDocuments(response.documents ?? []);
        }
      } catch (error) {
        if (!cancelled) {
          setSourceDocuments([]);
          setSourceDocumentsError(
            error instanceof Error
              ? error.message
              : "Failed to load source documents",
          );
        }
      } finally {
        if (!cancelled) {
          setSourceDocumentsLoading(false);
        }
      }
    };

    void loadSourceDocuments();

    return () => {
      cancelled = true;
    };
  }, [regulation]);

  const handleTabClick = (tab: RegulationTab) => {
    setActiveTab(tab);
  };

  const handleResultToggle = (resultId: string) => {
    setExpandedResults((prev) => ({
      ...prev,
      [resultId]: !prev[resultId],
    }));
  };

  const filteredSourceDocuments =
    sourceDocuments?.filter((doc) => matchesSourceFilter(doc, sourceDocumentFilter)) ??
    [];

  const renderTabContent = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <CircularProgress size={40} sx={{ color: "#0a829d" }} />
        </Box>
      );
    }

    switch (activeTab) {
      case "compliance_mindmap":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            <DetailSectionHeading
              title="Compliance Mindmap"
              note={`Interactive visualization of ${regulation.name} compliance requirements and relationships`}
            />
            <MindmapVisualization
              data={mindmapData}
              loading={mindmapLoading}
              error={mindmapError}
            />
          </Box>
        );

      case "penalties":
        return penaltiesData ? (
          <ResultsSection>
            <DetailSectionHeading
              title="Penalties Information"
              note={penaltiesData.regulation.name}
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {penaltiesData.regulation.enforcementRule.map(
                (rule, ruleIndex) => (
                    <ResultAccordion
                      key={ruleIndex}
                      expanded={expandedResults[`rule-${ruleIndex}`] || false}
                      onChange={() => handleResultToggle(`rule-${ruleIndex}`)}
                    >
                      <ResultAccordionSummary expandIcon={<ChevronDownIcon />}>
                        <CertificateIcon />
                        <ResultTitle>{rule.title}</ResultTitle>
                      </ResultAccordionSummary>
                      <AccordionDetails sx={{ padding: "16px" }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{ mb: 2, color: "#065b70" }}
                            >
                              Legal References
                            </Typography>
                            {Object.entries(rule.text).map(([key, value]) => (
                              <Box key={key} sx={{ mb: 1 }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 600, color: "#065b70" }}
                                >
                                  {key}
                                </Typography>
                                <Typography variant="body2">{value}</Typography>
                              </Box>
                            ))}
                          </Box>

                          <Box>
                            {((rule as any)?.effectiveDate ||
                              (rule as any)?.adjustmentBasis) && (
                              <Box
                                sx={{
                                  mb: 2,
                                  p: 2,
                                  backgroundColor: "#ffffff",
                                  borderRadius: 1,
                                  border: "1px solid #e8f8fc",
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{ mb: 1, color: "#065b70" }}
                                >
                                  Effective Details
                                </Typography>
                                {(rule as any)?.effectiveDate && (
                                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Effective Date:</strong>{" "}
                                    {(rule as any).effectiveDate}
                                  </Typography>
                                )}
                                {(rule as any)?.adjustmentBasis && (
                                  <Typography variant="body2">
                                    <strong>Adjustment Basis:</strong>{" "}
                                    {(rule as any).adjustmentBasis}
                                  </Typography>
                                )}
                              </Box>
                            )}
                            <Typography
                              variant="h6"
                              sx={{ mb: 2, color: "#065b70" }}
                            >
                              Penalty Tiers
                            </Typography>
                            {rule.penaltyTiers.map((tier, tierIndex) => (
                              <Box
                                key={tierIndex}
                                sx={{
                                  mb: 2,
                                  p: 2,
                                  backgroundColor: "#f9feff",
                                  borderRadius: 1,
                                  border: "1px solid #e8f8fc",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 2,
                                    alignItems: "center",
                                    mb: 1,
                                  }}
                                >
                                  <Chip
                                    label={`Tier ${tier.tier}`}
                                    size="small"
                                    sx={{
                                      backgroundColor: "#065b70",
                                      color: "white",
                                    }}
                                  />
                                  <Chip label={tier.culpability} size="small" />
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: "#065b70" }}
                                  >
                                    $
                                    {tier.minPenalty != null
                                      ? Number(tier.minPenalty).toLocaleString()
                                      : "N/A"}{" "}
                                    - $
                                    {tier.maxPenalty != null
                                      ? Number(tier.maxPenalty).toLocaleString()
                                      : "N/A"}
                                  </Typography>
                                </Box>
                                <Typography variant="body2">
                                  {formatCurrency(tier.text)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                          {Boolean((rule as any)?.fines) && (
                            <Box
                              sx={{
                                mb: 2,
                                p: 2,
                                backgroundColor: "#ffffff",
                                borderRadius: 1,
                                border: "1px solid #e8f8fc",
                              }}
                            >
                              <Typography
                                variant="h6"
                                sx={{ mb: 1, color: "#065b70" }}
                              >
                                Fines Summary
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  flexWrap: "wrap",
                                }}
                              >
                                {Object.entries((rule as any).fines).map(
                                  ([key, val]) => (
                                    <Chip
                                      key={key}
                                      label={`${key}: ${
                                        typeof val === "number"
                                          ? `$${Number(val).toLocaleString()}`
                                          : key === "effectiveDate" &&
                                            !isNaN(Date.parse(String(val)))
                                          ? formatDate(String(val))
                                          : String(val)
                                      }`}
                                    />
                                  )
                                )}
                              </Box>
                            </Box>
                          )}

                          <Box>
                            <Typography
                              variant="h6"
                              sx={{ mb: 2, color: "#065b70" }}
                            >
                              Mitigation Factors
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              {rule.mitigations.abatementImpact}
                            </Typography>
                            <Box component="ul" sx={{ pl: 2 }}>
                              {rule.mitigations.factorsConsidered.map(
                                (factor, index) => (
                                  <li key={index}>
                                    <Typography variant="body2">
                                      {factor}
                                    </Typography>
                                  </li>
                                )
                              )}
                            </Box>
                          </Box>

                          {Boolean((rule as any)?.proceduralRights) && (
                            <Box
                              sx={{
                                mb: 2,
                                p: 2,
                                backgroundColor: "#ffffff",
                                borderRadius: 1,
                                border: "1px solid #e8f8fc",
                              }}
                            >
                              <Typography
                                variant="h6"
                                sx={{ mb: 1, color: "#065b70" }}
                              >
                                Procedural Rights
                              </Typography>
                              {Boolean(
                                (rule as any).proceduralRights.notice
                              ) && (
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Notice:</strong>{" "}
                                  {(rule as any).proceduralRights.notice}
                                </Typography>
                              )}
                              {Boolean(
                                (rule as any).proceduralRights.contest
                              ) && (
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Contest:</strong>{" "}
                                  {(rule as any).proceduralRights.contest}
                                </Typography>
                              )}
                              {Boolean(
                                (rule as any).proceduralRights.appeal
                              ) && (
                                <Typography variant="body2">
                                  <strong>Appeal:</strong>{" "}
                                  {(rule as any).proceduralRights.appeal}
                                </Typography>
                              )}
                            </Box>
                          )}

                          {Boolean((rule as any)?.agencySpecificNotes) && (
                            <Box
                              sx={{
                                mb: 2,
                                p: 2,
                                backgroundColor: "#ffffff",
                                borderRadius: 1,
                                border: "1px solid #e8f8fc",
                              }}
                            >
                              <Typography
                                variant="h6"
                                sx={{ mb: 1, color: "#065b70" }}
                              >
                                Agency Specific Notes
                              </Typography>
                              <Typography variant="body2">
                                {(rule as any).agencySpecificNotes}
                              </Typography>
                            </Box>
                          )}

                          <Box>
                            <Typography
                              variant="h6"
                              sx={{ mb: 2, color: "#065b70" }}
                            >
                              Official Sources
                            </Typography>
                            {rule.officialSources.map((source, index) => (
                              <Link
                                key={index}
                                href={source}
                                target="_blank"
                                sx={{ display: "block", mb: 1 }}
                              >
                                {source}
                              </Link>
                            ))}
                          </Box>
                        </Box>
                      </AccordionDetails>
                    </ResultAccordion>
                )
              )}
            </Box>
          </ResultsSection>
        ) : (
          <Typography>No penalties data available</Typography>
        );

      case "trainings":
        return trainingsData ? (
          <ResultsSection>
            <DetailSectionHeading
              title="Training Resources"
              note={trainingsData.regulation.group}
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <ResultAccordion
                expanded={expandedResults["trainings-list"] || false}
                onChange={() => handleResultToggle("trainings-list")}
              >
                <ResultAccordionSummary expandIcon={<ChevronDownIcon />}>
                  <CertificateIcon />
                  <ResultTitle>Training Programs</ResultTitle>
                </ResultAccordionSummary>
                <AccordionDetails sx={{ padding: "16px" }}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {trainingsData.trainings.map((training, index) => (
                      <Box
                        key={index}
                        sx={{
                          p: 2,
                          backgroundColor: "#f9feff",
                          borderRadius: 1,
                          border: "1px solid #e8f8fc",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 1,
                          }}
                        >
                          <Typography variant="h6" sx={{ color: "#065b70" }}>
                            {training.title}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Chip label={training.format} size="small" />
                            <Chip
                              label={training.level}
                              size="small"
                              color={
                                training.level === "basic"
                                  ? "default"
                                  : training.level === "intermediate"
                                  ? "primary"
                                  : "secondary"
                              }
                            />
                            {training.certification && (
                              <Chip
                                label="Certified"
                                size="small"
                                color="success"
                              />
                            )}
                          </Box>
                        </Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {training.description}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
                          <Typography variant="body2">
                            <strong>Provider:</strong> {training.provider}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Duration:</strong> {training.duration}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Cost:</strong> {training.cost}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                          <Typography variant="body2">
                            <strong>Target Roles:</strong>
                          </Typography>
                          {training.targetRoles
                            .slice(0, 3)
                            .map((role, roleIndex) => (
                              <Chip
                                key={roleIndex}
                                label={role}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          {training.targetRoles.length > 3 && (
                            <Chip
                              label={`+${training.targetRoles.length - 3} more`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                        {training.url && (
                          <Link
                            href={training.url}
                            target="_blank"
                            sx={{ fontSize: "14px" }}
                          >
                            Access Training →
                          </Link>
                        )}
                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </ResultAccordion>

              <ResultAccordion
                expanded={expandedResults["mandatory-training"] || false}
                onChange={() => handleResultToggle("mandatory-training")}
                sx={{ mt: 2 }}
              >
                <ResultAccordionSummary expandIcon={<ChevronDownIcon />}>
                  <CertificateIcon />
                  <ResultTitle>
                    Mandatory Training Requirements
                  </ResultTitle>
                </ResultAccordionSummary>
                <AccordionDetails sx={{ padding: "16px" }}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {trainingsData.compliance.mandatoryTraining.map(
                      (requirement, index) => (
                        <Box
                          key={index}
                          sx={{
                            p: 2,
                            backgroundColor: "#f9feff",
                            borderRadius: 1,
                            border: "1px solid #e8f8fc",
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{ color: "#065b70", mb: 1 }}
                          >
                            {requirement.role}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Frequency:</strong> {requirement.frequency}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Required Training:</strong>
                          </Typography>
                          <Box component="ul" sx={{ pl: 2 }}>
                            {requirement.requiredTraining.map(
                              (training, trainingIndex) => (
                                <li key={trainingIndex}>
                                  <Typography variant="body2">
                                    {training}
                                  </Typography>
                                </li>
                              )
                            )}
                          </Box>
                        </Box>
                      )
                    )}
                  </Box>
                </AccordionDetails>
              </ResultAccordion>

              <ResultAccordion
                expanded={expandedResults["certifications"] || false}
                onChange={() => handleResultToggle("certifications")}
                sx={{ mt: 2 }}
              >
                <ResultAccordionSummary expandIcon={<ChevronDownIcon />}>
                  <CertificateIcon />
                  <ResultTitle>Certifications</ResultTitle>
                </ResultAccordionSummary>
                <AccordionDetails sx={{ padding: "16px" }}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {trainingsData.certifications.map((cert, index) => (
                      <Box
                        key={index}
                        sx={{
                          p: 2,
                          backgroundColor: "#f9feff",
                          borderRadius: 1,
                          border: "1px solid #e8f8fc",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ color: "#065b70", mb: 1 }}
                        >
                          {cert.name}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Issuer:</strong> {cert.issuer}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Validity:</strong> {cert.validityPeriod}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Renewal:</strong> {cert.renewalRequirements}
                        </Typography>
                        {cert.url && (
                          <Link
                            href={cert.url}
                            target="_blank"
                            sx={{ fontSize: "14px" }}
                          >
                            Learn More →
                          </Link>
                        )}
                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </ResultAccordion>
            </Box>
          </ResultsSection>
        ) : (
          <Typography>No trainings data available</Typography>
        );

      case "fine_analysis":
        return fineAnalysisData ? (
          <ResultsSection>
            <DetailSectionHeading
              title="Fine Analysis"
              note="Fine Analysis Results"
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <ResultAccordion
                  expanded={expandedResults["enforcement-authority"] || false}
                  onChange={() => handleResultToggle("enforcement-authority")}
                >
                  <ResultAccordionSummary expandIcon={<ChevronDownIcon />}>
                    <CertificateIcon />
                    <ResultTitle>
                      Enforcement Authority
                    </ResultTitle>
                  </ResultAccordionSummary>
                  <AccordionDetails sx={{ padding: "16px" }}>
                    <Typography variant="body1">
                      {fineAnalysisData.enforcement.authority}
                    </Typography>
                  </AccordionDetails>
                </ResultAccordion>

                <ResultAccordion
                  expanded={expandedResults["enforcement-cases"] || false}
                  onChange={() => handleResultToggle("enforcement-cases")}
                >
                  <ResultAccordionSummary expandIcon={<ChevronDownIcon />}>
                    <CertificateIcon />
                    <ResultTitle>
                      Recent Enforcement Cases
                    </ResultTitle>
                  </ResultAccordionSummary>
                  <AccordionDetails sx={{ padding: "16px" }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {(
                        fineAnalysisData.enforcement?.enforcementAction?.filter(
                          Boolean
                        ) ?? []
                      ).map((action: any, index: number) => (
                        <Box
                          key={index}
                          sx={{
                            p: 2,
                            backgroundColor: "#f9feff",
                            borderRadius: 1,
                            border: "1px solid #e8f8fc",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 2,
                            }}
                          >
                            <Typography variant="h6" sx={{ color: "#065b70" }}>
                              {action?.company ?? "N/A"}
                            </Typography>
                            <Chip
                              label={`$${(
                                (action?.penalty?.amount ?? 0) as number
                              ).toLocaleString()}`}
                              sx={{
                                backgroundColor: "#ffebee",
                                color: "#c62828",
                                fontWeight: 600,
                              }}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Case:</strong>{" "}
                            {action?.caseDetails?.caseName ?? "N/A"} (
                            {action?.caseDetails?.year ?? "—"})
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Violation:</strong>{" "}
                            {action?.violation?.type ?? "N/A"}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Legal Basis:</strong>{" "}
                            {action?.violation?.legalBasis ?? "N/A"}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 2 }}>
                            <strong>Settlement Basis:</strong>{" "}
                            {action?.penalty?.basis ?? "N/A"}
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{ mb: 1, fontWeight: 600 }}
                          >
                            Compliance Obligations:
                          </Typography>
                          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                            {(action?.complianceObligations ?? []).map(
                              (obligation: string, obligationIndex: number) => (
                                <li key={obligationIndex}>
                                  <Typography variant="body2">
                                    {obligation}
                                  </Typography>
                                </li>
                              )
                            )}
                          </Box>

                          {(action?.officialSources ?? []).length > 0 && (
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ mb: 1, fontWeight: 600 }}
                              >
                                Official Sources:
                              </Typography>
                              {(action?.officialSources ?? []).map(
                                (source: string, sourceIndex: number) => (
                                  <Link
                                    key={sourceIndex}
                                    href={source}
                                    target="_blank"
                                    sx={{
                                      display: "block",
                                      mb: 1,
                                      fontSize: "14px",
                                    }}
                                  >
                                    {source}
                                  </Link>
                                )
                              )}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </AccordionDetails>
                </ResultAccordion>

                <ResultAccordion
                  expanded={expandedResults["trend-analysis"] || false}
                  onChange={() => handleResultToggle("trend-analysis")}
                >
                  <ResultAccordionSummary expandIcon={<ChevronDownIcon />}>
                    <CertificateIcon />
                    <ResultTitle>Trend Analysis</ResultTitle>
                  </ResultAccordionSummary>
                  <AccordionDetails sx={{ padding: "16px" }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {Object.entries(
                        fineAnalysisData.enforcement.trendAnalysis
                      ).map(([year, data]) => (
                        <Box
                          key={year}
                          sx={{
                            p: 2,
                            backgroundColor: "#f9feff",
                            borderRadius: 1,
                            border: "1px solid #e8f8fc",
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{ color: "#065b70", mb: 1 }}
                          >
                            {year}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
                            <Chip
                              label={`Total: $${Number(
                                data?.totalFines ?? 0
                              ).toLocaleString()}`}
                            />
                            <Chip
                              label={`Largest: $${Number(
                                data?.largestFine?.amount ?? 0
                              ).toLocaleString()}`}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Largest Fine:</strong>{" "}
                            {data?.largestFine?.company ?? "N/A"}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Industry Patterns:</strong>{" "}
                            {data.industryPatterns}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </AccordionDetails>
              </ResultAccordion>
            </Box>
          </ResultsSection>
        ) : (
          <Typography>No fine analysis data available</Typography>
        );

      case "source_documents":
        return (
          <ResultsSection>
            <DetailSectionHeading
              title="Official Source Documents"
              note={`Federal Register source material and preamble sections linked to ${regulation.name}.`}
            />
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Search document number, citation, CFR part, heading, or anchor"
                value={sourceDocumentFilter}
                onChange={(event) => setSourceDocumentFilter(event.target.value)}
              />
            </Box>

            {sourceDocumentsLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "160px",
                }}
              >
                <CircularProgress size={36} sx={{ color: "#0a829d" }} />
              </Box>
            ) : sourceDocumentsError ? (
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "#fff7ed",
                  border: "1px solid #fed7aa",
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" sx={{ color: "#9a3412" }}>
                  {sourceDocumentsError}
                </Typography>
              </Box>
            ) : filteredSourceDocuments.length === 0 ? (
              <Typography variant="body2" sx={{ color: "#737373" }}>
                No source documents are available for this regulation yet.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {filteredSourceDocuments.map((doc, docIndex) => {
                  const docKey = sourceDocumentKey(doc, docIndex);
                  const sections = doc.sections ?? [];
                  return (
                    <ResultAccordion
                      key={docKey}
                      expanded={expandedResults[`source-doc-${docKey}`] || false}
                      onChange={() => handleResultToggle(`source-doc-${docKey}`)}
                    >
                      <ResultAccordionSummary expandIcon={<ChevronDownIcon />}>
                        <CertificateIcon />
                        <Box sx={{ minWidth: 0 }}>
                          <ResultTitle>{doc.title}</ResultTitle>
                          <Typography
                            variant="body2"
                            sx={{ color: "#737373", mt: 0.5 }}
                          >
                            {doc.citation || doc.document_number}
                            {doc.publication_date
                              ? ` · Published ${formatDate(doc.publication_date)}`
                              : ""}
                            {doc.effective_date
                              ? ` · Effective ${formatDate(doc.effective_date)}`
                              : ""}
                          </Typography>
                        </Box>
                      </ResultAccordionSummary>
                      <AccordionDetails sx={{ padding: "16px" }}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            flexWrap: "wrap",
                            mb: 2,
                          }}
                        >
                          <Chip label={doc.source_system} size="small" />
                          <Chip label={doc.document_number} size="small" />
                          {doc.citation ? (
                            <Chip label={doc.citation} size="small" />
                          ) : null}
                          {(doc.cfr_references ?? []).map((ref) => (
                            <Chip key={ref} label={ref} size="small" />
                          ))}
                        </Box>
                        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                          {(doc.source_url || doc.html_url) && (
                            <Link
                              href={doc.source_url || doc.html_url || undefined}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ fontSize: "14px" }}
                            >
                              View official document
                            </Link>
                          )}
                          {doc.pdf_url && (
                            <Link
                              href={doc.pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ fontSize: "14px" }}
                            >
                              View PDF
                            </Link>
                          )}
                        </Box>

                        {sections.length === 0 ? (
                          <Typography variant="body2" sx={{ color: "#737373" }}>
                            No section anchors are available for this document yet.
                          </Typography>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "12px",
                            }}
                          >
                            {sections.map((section, sectionIndex) => {
                              const sectionKey = sourceSectionKey(
                                section,
                                sectionIndex,
                              );
                              const headingPath = formatHeadingPath(
                                section.heading_path,
                              );
                              const sectionText = getSourceSectionText(section);
                              return (
                                <ResultAccordion
                                  key={sectionKey}
                                  expanded={
                                    expandedResults[
                                      `source-section-${docKey}-${sectionKey}`
                                    ] || false
                                  }
                                  onChange={() =>
                                    handleResultToggle(
                                      `source-section-${docKey}-${sectionKey}`,
                                    )
                                  }
                                >
                                  <ResultAccordionSummary
                                    expandIcon={<ChevronDownIcon />}
                                  >
                                    <CertificateIcon />
                                    <Box sx={{ minWidth: 0 }}>
                                      <ResultTitle>
                                        {section.heading ||
                                          section.anchor_id ||
                                          "Source section"}
                                      </ResultTitle>
                                      <Typography
                                        variant="body2"
                                        sx={{ color: "#737373", mt: 0.5 }}
                                      >
                                        {section.anchor_id
                                          ? `#${section.anchor_id}`
                                          : ""}
                                        {headingPath
                                          ? `${section.anchor_id ? " · " : ""}${headingPath}`
                                          : ""}
                                      </Typography>
                                    </Box>
                                  </ResultAccordionSummary>
                                  <AccordionDetails sx={{ padding: "16px" }}>
                                    {(section.cfr_references ?? []).length > 0 ? (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          gap: 1,
                                          flexWrap: "wrap",
                                          mb: 1.5,
                                        }}
                                      >
                                        {(section.cfr_references ?? []).map((ref) => (
                                          <Chip key={ref} label={ref} size="small" />
                                        ))}
                                      </Box>
                                    ) : null}
                                    <Typography
                                      variant="body2"
                                      sx={{ whiteSpace: "pre-wrap", mb: 1.5 }}
                                    >
                                      {sectionText || "No text has been imported yet."}
                                    </Typography>
                                    {section.source_url ? (
                                      <Link
                                        href={section.source_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ fontSize: "14px" }}
                                      >
                                        Open this section at source
                                      </Link>
                                    ) : null}
                                  </AccordionDetails>
                                </ResultAccordion>
                              );
                            })}
                          </Box>
                        )}
                      </AccordionDetails>
                    </ResultAccordion>
                  );
                })}
              </Box>
            )}
          </ResultsSection>
        );

      default:
        return <Typography>Content loading...</Typography>;
    }
  };

  return (
    <ContentContainer>
      <ContentWrapper>
        <MainContent>
          {/* {onBack && (
            <BackButton onClick={onBack} startIcon={<span>←</span>}>
              Back to Regulations
            </BackButton>
          )} */}

          {/* Breadcrumbs */}
          <Breadcrumbs>
            <BreadcrumbLink
              onClick={() => {
                try {
                  sessionStorage.setItem("spaces_target_tab", "facilities");
                } catch (_) {}
                onBack && onBack();
              }}
            >
              Regulations Library
            </BreadcrumbLink>
            <span> › </span>
            <BreadcrumbLink
              onClick={() => {
                try {
                  sessionStorage.setItem("spaces_target_tab", "regulations");
                } catch (_) {}
                onBack && onBack();
              }}
            >
              Regulations
            </BreadcrumbLink>
            <span> › </span>
            <BreadcrumbLink className="active">
              {regulation.name} Regulations
            </BreadcrumbLink>
          </Breadcrumbs>

          {/* Header */}
          <Box>
            <HeaderTitle>
              <RegulationLogo>
                {regulation.logo_url ? (
                  <img
                    src={regulation.logo_url}
                    alt={`${regulation.name} logo`}
                    onError={(e) => {
                      // Fallback to emoji if image fails to load
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                      const parent = target.parentElement;
                      if (parent) {
                        parent.textContent = regulation.logo_emoji;
                      }
                    }}
                  />
                ) : (
                  regulation.logo_emoji
                )}
              </RegulationLogo>
              <Title>{regulation.name} Regulations</Title>
            </HeaderTitle>
            <Subtitle>{regulation.description}</Subtitle>

            {/* Tabs */}
            <TabsContainer>
              <TabButton
                $active={activeTab === "compliance_mindmap"}
                onClick={() => handleTabClick("compliance_mindmap")}
              >
                Compliance Mindmap
              </TabButton>
              <TabButton
                $active={activeTab === "penalties"}
                onClick={() => handleTabClick("penalties")}
              >
                Penalties
              </TabButton>
              <TabButton
                $active={activeTab === "trainings"}
                onClick={() => handleTabClick("trainings")}
              >
                Trainings
              </TabButton>
              <TabButton
                $active={activeTab === "fine_analysis"}
                onClick={() => handleTabClick("fine_analysis")}
              >
                Fine Analysis
              </TabButton>
              {showSourceDocumentsTab && (
                <TabButton
                  $active={activeTab === "source_documents"}
                  onClick={() => handleTabClick("source_documents")}
                >
                  Source Documents
                </TabButton>
              )}
            </TabsContainer>
          </Box>

          {/* Tab Content */}
          <TabContentContainer>{renderTabContent()}</TabContentContainer>
        </MainContent>
      </ContentWrapper>
    </ContentContainer>
  );
}
